import { c as _c } from "react/compiler-runtime";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { invalidateDoseInventory } from "@/lib/billing/invalidate-dose-inventory";
import { parseError } from "@/lib/parse-error";
import type { AdjustmentCreate, WriteOffCreate } from "@/types/adjustment";
import type { ChargeDetailsUpdate, ChargeTableItem, ChargeTimelineItem } from "@/types/charge";
import { ChargeStatus } from "@/types/enums";
import { RefundItemAction, RefundType } from "@/types/enums/refund";
import type { Page } from "@/types/pagination";
import type { RefundCreate, RefundPreviewRequest } from "@/types/refund";
import type { ReversalOptions, ReversalPreview } from "@/types/reversal";
import type { TipUpdate } from "@/types/tip";
import { createAdjustment, writeOffCharge } from "./adjustment";
import { deleteCharge, fetchChargeReversalPreview, fetchChargeTable, fetchChargeTimeline, refundChargePayment, updateChargeComment, updateChargeDetails, updateSoldByProvider } from "./charge";
import { deletePayment, getPackageRemainingValues, getRefundContext, movePayment, movePaymentToLocation, previewItemRefund, removePayment } from "./payment";
import { updateTip } from "./tip";
import { chargesQueryKeys } from "@/query/billing/charges";
export const chargeQueryKeys = {
  all: ["billing", "charge"] as const,
  table: (patientId: string) => [...chargeQueryKeys.all, "table", patientId] as const,
  tableList: (filters: Record<string, unknown>) => [...chargeQueryKeys.all, "tableList", filters] as const,
  timeline: (chargeId?: string, paymentId?: string) => [...chargeQueryKeys.all, "timeline", {
    chargeId,
    paymentId
  }] as const,
  reversalPreview: (chargeId: string) => [...chargeQueryKeys.all, "reversalPreview", chargeId] as const,
  packageRemainingValues: (paymentId: string) => [...chargeQueryKeys.all, "packageRemainingValues", paymentId] as const,
  refundContext: (paymentId: string) => [...chargeQueryKeys.all, "refundContext", paymentId] as const,
  refundPreview: (paymentId: string, request: RefundPreviewRequest | null) => [...chargeQueryKeys.all, "refundPreview", paymentId, request] as const
};
export function useChargeReversalPreview(chargeId) {
  const $ = _c(8);
  let t0;
  if ($[0] !== chargeId) {
    t0 = chargeQueryKeys.reversalPreview(chargeId ?? "");
    $[0] = chargeId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== chargeId) {
    t1 = () => fetchChargeReversalPreview(chargeId);
    $[2] = chargeId;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const t2 = !!chargeId;
  let t3;
  if ($[4] !== t0 || $[5] !== t1 || $[6] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: t1,
      enabled: t2,
      select: _temp
    };
    $[4] = t0;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return useQuery(t3);
}
function _temp(data) {
  return data;
}
export function useRefundContext(paymentId) {
  const $ = _c(8);
  let t0;
  if ($[0] !== paymentId) {
    t0 = chargeQueryKeys.refundContext(paymentId ?? "");
    $[0] = paymentId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== paymentId) {
    t1 = () => getRefundContext(paymentId);
    $[2] = paymentId;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const t2 = !!paymentId;
  let t3;
  if ($[4] !== t0 || $[5] !== t1 || $[6] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: t1,
      enabled: t2
    };
    $[4] = t0;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return useQuery(t3);
}
export function useRefundPreview(paymentId, request) {
  const $ = _c(10);
  let t0;
  if ($[0] !== paymentId || $[1] !== request) {
    t0 = chargeQueryKeys.refundPreview(paymentId ?? "", request);
    $[0] = paymentId;
    $[1] = request;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] !== paymentId || $[4] !== request) {
    t1 = async () => {
      ;
      try {
        return await previewItemRefund(paymentId, request);
      } catch (t2) {
        const error = t2;
        throw new Error(await parseError(error));
      }
    };
    $[3] = paymentId;
    $[4] = request;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const t2 = !!paymentId && !!request && request.items.length > 0;
  let t3;
  if ($[6] !== t0 || $[7] !== t1 || $[8] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: t1,
      enabled: t2
    };
    $[6] = t0;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return useQuery(t3);
}
export function usePackageRemainingValues(paymentId) {
  const $ = _c(8);
  let t0;
  if ($[0] !== paymentId) {
    t0 = chargeQueryKeys.packageRemainingValues(paymentId ?? "");
    $[0] = paymentId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== paymentId) {
    t1 = () => getPackageRemainingValues(paymentId);
    $[2] = paymentId;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const t2 = !!paymentId;
  let t3;
  if ($[4] !== t0 || $[5] !== t1 || $[6] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: t1,
      enabled: t2
    };
    $[4] = t0;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return useQuery(t3);
}
export const chargeQueryOptions = {
  table: (patientId: string) => ({
    queryKey: chargeQueryKeys.table(patientId),
    queryFn: () => fetchChargeTable({
      patientId
    }),
    select: (data: Page<ChargeTableItem> | null) => data?.data ?? []
  }),
  timeline: (chargeId?: string, paymentId?: string) => ({
    queryKey: chargeQueryKeys.timeline(chargeId, paymentId),
    queryFn: () => fetchChargeTimeline(chargeId, paymentId),
    select: (data: ChargeTimelineItem[] | null) => data ?? [],
    enabled: !!(chargeId || paymentId),
    staleTime: 1000 * 60 * 5 // 5 minutes - timeline data doesn't change frequently
  })
};
export const useChargeMutations = () => {
  const $ = _c(56);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = (chargeId, paymentId) => {
      if (chargeId || paymentId) {
        queryClient.invalidateQueries({
          queryKey: chargeQueryKeys.timeline(chargeId, paymentId)
        });
      }
      queryClient.invalidateQueries({
        queryKey: [...chargeQueryKeys.all, "timeline"]
      });
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const invalidateTimeline = t0;
  let t1;
  if ($[2] !== queryClient) {
    t1 = () => {
      queryClient.invalidateQueries({
        queryKey: ["available-credit"]
      });
      queryClient.invalidateQueries({
        queryKey: ["patient-credits"]
      });
      queryClient.invalidateQueries({
        queryKey: ["patient-credit-transactions"]
      });
    };
    $[2] = queryClient;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const invalidateAvailableCredit = t1;
  let t2;
  if ($[4] !== invalidateTimeline || $[5] !== queryClient) {
    t2 = {
      mutationFn: _temp2,
      onSuccess: (data, variables) => {
        invalidateTimeline(variables.chargeId);
        queryClient.invalidateQueries({
          queryKey: chargeQueryKeys.all
        });
      }
    };
    $[4] = invalidateTimeline;
    $[5] = queryClient;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const t3 = useMutation(t2);
  let t4;
  if ($[7] !== invalidateTimeline || $[8] !== queryClient) {
    t4 = {
      mutationFn: _temp3,
      onSuccess: (data_0, variables_0) => {
        invalidateTimeline(variables_0.chargeId);
        queryClient.invalidateQueries({
          queryKey: chargeQueryKeys.all
        });
      }
    };
    $[7] = invalidateTimeline;
    $[8] = queryClient;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const t5 = useMutation(t4);
  let t6;
  if ($[10] !== invalidateAvailableCredit || $[11] !== invalidateTimeline || $[12] !== queryClient) {
    t6 = {
      mutationFn: _temp4,
      onSuccess: (data_1, variables_1) => {
        invalidateTimeline(variables_1.chargeId, variables_1.refund.paymentId);
        queryClient.invalidateQueries({
          queryKey: chargeQueryKeys.all,
          predicate: _temp5
        });
        const refund_0 = variables_1.refund;
        const restoresInventory = refund_0.type === RefundType.ITEMS ? refund_0.items.some(_temp6) : refund_0.reversalOptions?.reverseInventory === true;
        if (restoresInventory) {
          invalidateDoseInventory(queryClient);
        }
        invalidateAvailableCredit();
      }
    };
    $[10] = invalidateAvailableCredit;
    $[11] = invalidateTimeline;
    $[12] = queryClient;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const t7 = useMutation(t6);
  let t8;
  if ($[14] !== invalidateAvailableCredit || $[15] !== invalidateTimeline || $[16] !== queryClient) {
    t8 = {
      mutationFn: _temp7,
      onSuccess: (data_2, variables_2) => {
        invalidateTimeline(variables_2.chargeId);
        queryClient.invalidateQueries({
          queryKey: chargeQueryKeys.all
        });
        if (variables_2.reversalOptions?.reverseInventory !== false) {
          invalidateDoseInventory(queryClient);
        }
        invalidateAvailableCredit();
      }
    };
    $[14] = invalidateAvailableCredit;
    $[15] = invalidateTimeline;
    $[16] = queryClient;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const t9 = useMutation(t8);
  let t10;
  if ($[18] !== invalidateAvailableCredit || $[19] !== invalidateTimeline || $[20] !== queryClient) {
    t10 = {
      mutationFn: _temp8,
      onSuccess: (data_3, variables_3) => {
        invalidateTimeline(undefined, variables_3.paymentId);
        queryClient.invalidateQueries({
          queryKey: chargeQueryKeys.all
        });
        invalidateDoseInventory(queryClient);
        invalidateAvailableCredit();
      }
    };
    $[18] = invalidateAvailableCredit;
    $[19] = invalidateTimeline;
    $[20] = queryClient;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  const t11 = useMutation(t10);
  let t12;
  if ($[22] !== invalidateTimeline || $[23] !== queryClient) {
    t12 = {
      mutationFn: _temp9,
      onSuccess: (data_4, variables_4) => {
        invalidateTimeline(undefined, variables_4.paymentId);
        queryClient.invalidateQueries({
          queryKey: chargeQueryKeys.all
        });
      }
    };
    $[22] = invalidateTimeline;
    $[23] = queryClient;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  const t13 = useMutation(t12);
  let t14;
  if ($[25] !== invalidateTimeline || $[26] !== queryClient) {
    t14 = {
      mutationFn: _temp10,
      onSuccess: data_5 => {
        if (data_5?.chargeId) {
          invalidateTimeline(data_5.chargeId);
        }
        queryClient.invalidateQueries({
          queryKey: chargeQueryKeys.all
        });
      }
    };
    $[25] = invalidateTimeline;
    $[26] = queryClient;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  const t15 = useMutation(t14);
  let t16;
  if ($[28] !== invalidateTimeline || $[29] !== queryClient) {
    t16 = {
      mutationFn: _temp11,
      onSuccess: (data_6, variables_5) => {
        invalidateTimeline(variables_5.chargeId);
        queryClient.invalidateQueries({
          queryKey: chargeQueryKeys.all
        });
      }
    };
    $[28] = invalidateTimeline;
    $[29] = queryClient;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  const t17 = useMutation(t16);
  let t18;
  if ($[31] !== invalidateTimeline || $[32] !== queryClient) {
    t18 = {
      mutationFn: _temp12,
      onSuccess: (data_7, variables_6) => {
        if (data_7) {
          const updateCachedCharge = cachedPage => {
            if (!cachedPage) {
              return cachedPage;
            }
            return {
              ...cachedPage,
              data: cachedPage.data.map(charge => charge.id === variables_6.chargeId ? {
                ...charge,
                status: ChargeStatus.WRITE_OFF,
                totalOutstanding: 0,
                adjustments: [...charge.adjustments.filter(adjustment_0 => adjustment_0.id !== data_7.id), data_7]
              } : charge)
            };
          };
          queryClient.setQueriesData({
            queryKey: chargesQueryKeys.all
          }, updateCachedCharge);
          queryClient.setQueriesData({
            queryKey: [...chargeQueryKeys.all, "table"]
          }, updateCachedCharge);
        }
        invalidateTimeline(variables_6.chargeId);
        queryClient.invalidateQueries({
          queryKey: chargeQueryKeys.all
        });
      }
    };
    $[31] = invalidateTimeline;
    $[32] = queryClient;
    $[33] = t18;
  } else {
    t18 = $[33];
  }
  const t19 = useMutation(t18);
  let t20;
  if ($[34] !== invalidateTimeline || $[35] !== queryClient) {
    t20 = {
      mutationFn: _temp13,
      onSuccess: (data_8, variables_7) => {
        invalidateTimeline(undefined, variables_7.paymentId);
        queryClient.invalidateQueries({
          queryKey: chargeQueryKeys.all
        });
      }
    };
    $[34] = invalidateTimeline;
    $[35] = queryClient;
    $[36] = t20;
  } else {
    t20 = $[36];
  }
  const t21 = useMutation(t20);
  let t22;
  if ($[37] !== invalidateTimeline || $[38] !== queryClient) {
    t22 = {
      mutationFn: _temp14,
      onSuccess: (data_9, variables_8) => {
        invalidateTimeline(undefined, variables_8.paymentId);
        queryClient.invalidateQueries({
          queryKey: chargeQueryKeys.all
        });
      }
    };
    $[37] = invalidateTimeline;
    $[38] = queryClient;
    $[39] = t22;
  } else {
    t22 = $[39];
  }
  const t23 = useMutation(t22);
  let t24;
  if ($[40] !== invalidateTimeline || $[41] !== queryClient) {
    t24 = {
      mutationFn: _temp15,
      onSuccess: (data_10, variables_9) => {
        invalidateTimeline(variables_9.chargeId);
        queryClient.invalidateQueries({
          queryKey: chargeQueryKeys.all
        });
      }
    };
    $[40] = invalidateTimeline;
    $[41] = queryClient;
    $[42] = t24;
  } else {
    t24 = $[42];
  }
  const t25 = useMutation(t24);
  let t26;
  if ($[43] !== t11 || $[44] !== t13 || $[45] !== t15 || $[46] !== t17 || $[47] !== t19 || $[48] !== t21 || $[49] !== t23 || $[50] !== t25 || $[51] !== t3 || $[52] !== t5 || $[53] !== t7 || $[54] !== t9) {
    t26 = {
      updateComment: t3,
      updateDetails: t5,
      refund: t7,
      deleteCharge: t9,
      deletePayment: t11,
      removePayment: t13,
      updateSoldByProvider: t15,
      createAdjustment: t17,
      writeOff: t19,
      movePayment: t21,
      movePaymentToLocation: t23,
      updateTip: t25
    };
    $[43] = t11;
    $[44] = t13;
    $[45] = t15;
    $[46] = t17;
    $[47] = t19;
    $[48] = t21;
    $[49] = t23;
    $[50] = t25;
    $[51] = t3;
    $[52] = t5;
    $[53] = t7;
    $[54] = t9;
    $[55] = t26;
  } else {
    t26 = $[55];
  }
  return t26;
};
function _temp2(t0) {
  const {
    chargeId: chargeId_0,
    comment
  } = t0;
  return updateChargeComment(chargeId_0, comment);
}
function _temp3(t0) {
  const {
    chargeId: chargeId_1,
    details
  } = t0;
  return updateChargeDetails(chargeId_1, details);
}
function _temp4(t0) {
  const {
    refund
  } = t0;
  return refundChargePayment(refund);
}
function _temp5(query) {
  return query.queryKey[2] !== "refundPreview";
}
function _temp6(item) {
  return item.reversalActions?.includes(RefundItemAction.RESTORE_INVENTORY);
}
function _temp7(t0) {
  const {
    chargeId: chargeId_2,
    reversalOptions
  } = t0;
  return deleteCharge(chargeId_2, reversalOptions);
}
function _temp8(t0) {
  const {
    paymentId: paymentId_0
  } = t0;
  return deletePayment(paymentId_0);
}
function _temp9(t0) {
  const {
    paymentId: paymentId_1
  } = t0;
  return removePayment(paymentId_1);
}
function _temp10(t0) {
  const {
    itemId,
    providerId,
    itemType
  } = t0;
  return updateSoldByProvider(itemId, providerId, itemType);
}
function _temp11(adjustment) {
  return createAdjustment(adjustment);
}
function _temp12(writeOff) {
  return writeOffCharge(writeOff);
}
function _temp13(t0) {
  const {
    paymentId: paymentId_2,
    newPatientId
  } = t0;
  return movePayment(paymentId_2, newPatientId);
}
function _temp14(t0) {
  const {
    paymentId: paymentId_3,
    newLocationId
  } = t0;
  return movePaymentToLocation(paymentId_3, newLocationId);
}
function _temp15(t0) {
  const {
    tipId,
    tipUpdate
  } = t0;
  return updateTip(tipId, tipUpdate);
}